import React from 'react';

import type { LaunchFieldSelection } from '@aline/common/launch-fields/selection';
import {
  formatLaunchSelectionValue,
  getCanAddLaunchSelection,
  getLaunchSelectionError,
  getLaunchSelectionHint,
  parseLaunchSelectionValue,
} from '@aline/common/launch-fields/selection';
import { Typography } from '@mui/material';
import { AlineCheckbox } from 'src/components/generic/AlineCheckbox';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  choices: {
    display: 'flex',
    flexDirection: 'column',
  },
  note: {
    fontSize: 12,
  },
  row: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

/**
 * A template checkbox field: one box per option, as the document shows it.
 * Boxes past the field's maximum are disabled, and a count outside its limits
 * is called out under the boxes, where launch also refuses it.
 */
export const LaunchSelectionChoices = React.memo(function LaunchSelectionChoices({
  label,
  onChange,
  options,
  selection,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  options: readonly { label: string; value: string }[];
  selection: LaunchFieldSelection;
  value: string;
}) {
  const { classes } = useStyles();
  const chosen = new Set(parseLaunchSelectionValue(value));
  const canAdd = getCanAddLaunchSelection(selection, chosen.size);
  const error = getLaunchSelectionError(selection, chosen.size);
  const note = error ?? getLaunchSelectionHint(selection);

  const toggle = (optionValue: string, checked: boolean) => {
    const next = new Set(chosen);
    if (checked) next.add(optionValue);
    else next.delete(optionValue);
    // Keep the template's option order, whatever order the boxes were ticked in.
    onChange(
      formatLaunchSelectionValue(
        options.flatMap(option => (next.has(option.value) ? [option.value] : []))
      )
    );
  };

  return (
    <div aria-label={label} className={classes.choices} role="group">
      {options.map(option => {
        const checked = chosen.has(option.value);
        return (
          <label key={option.value} className={classes.row}>
            <AlineCheckbox
              checked={checked}
              disabled={!checked && !canAdd}
              onChange={event => toggle(option.value, event.target.checked)}
            />
            <Typography>{option.label}</Typography>
          </label>
        );
      })}
      {note && (
        <Typography
          className={classes.note}
          color={error ? 'error' : 'textSecondary'}
          variant="body2"
        >
          {note}
        </Typography>
      )}
    </div>
  );
});
